/**
 * Pitch shift that preserves duration via SoundTouch (WSOLA).
 * @see https://github.com/cutterbl/SoundTouchJS
 */

import { processOffline } from '@soundtouchjs/audio-worklet';
import soundtouchProcessorUrl from '@soundtouchjs/audio-worklet/processor?url';

/** Apply subtle chorus to break up voice fingerprint (same duration). */
export function applySubtleChorus(
  input: Float32Array,
  sampleRate: number,
  mix: number
): Float32Array {
  if (mix <= 0) {
    return input;
  }

  const delaySamples = Math.max(1, Math.floor(sampleRate * 0.023));
  const output = input.slice();
  for (let i = delaySamples; i < input.length; i++) {
    output[i] =
      output[i]! * (1 - mix) + (input[i - delaySamples]! + input[i - delaySamples + 1]! * 0.5) * mix;
  }
  return output;
}

function applyChorusToBuffer(buffer: AudioBuffer, chorusMix: number): AudioBuffer {
  if (chorusMix <= 0) {
    return buffer;
  }

  const ctx = new OfflineAudioContext(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate
  );
  const out = ctx.createBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate);

  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const channel = applySubtleChorus(buffer.getChannelData(ch), buffer.sampleRate, chorusMix);
    out.copyToChannel(new Float32Array(channel), ch);
  }

  return out;
}

/** Shift pitch in semitones; output duration matches input (playbackRate 1.0). */
export async function pitchShiftAudioBufferPreserveDuration(
  buffer: AudioBuffer,
  semitones: number,
  chorusMix = 0
): Promise<AudioBuffer> {
  if (Math.abs(semitones) < 0.01 && chorusMix <= 0) {
    return buffer;
  }

  let processed = buffer;

  if (Math.abs(semitones) >= 0.01) {
    processed = await processOffline({
      input: buffer,
      processorUrl: soundtouchProcessorUrl,
      pitch: 1,
      pitchSemitones: semitones,
      playbackRate: 1,
      stretchParameters: {
        overlapMs: 12,
        quickSeek: false
      }
    });
  }

  return applyChorusToBuffer(processed, chorusMix);
}
